import {SERVICE_NAMES} from '../constants';
import {createApiCall} from '../utils';
import type {ServiceName} from '../constants';
import type {
    ApiCallOptions,
    VerificationRequest,
    ProviderRequest,
    AccountProviderRequest,
    UpdateAccountProviderRequest,
    VerificationTokenRequest,
    ApiResponseWrapperVerificationView,
    ApiResponseWrapperAccountProviderView,
    ApiResponseWrapperProviderView,
    ApiResponseWrapperVerificationTokenView,
    PageViewProviderView,
    PageViewAccountProviderView,
    PaginationParams,
    VerificationRequestTokenData
} from './api.d';

const createEsvApiCall = <T = any>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    endpoint: string
) => createApiCall<T>(SERVICE_NAMES.ESV as ServiceName, method, endpoint);

export const getVerificationRequestToken = (
    transactionId: string,
    documentId: string,
    data: VerificationTokenRequest,
    options: ApiCallOptions = {}
) => createEsvApiCall<VerificationRequestTokenData>(
    'POST',
    `transactions/${transactionId}/documents/${documentId}/verificationRequestToken`
)({...options, data});

export const esvApi = {
    getVerification: ({
        transactionId,
        recipientId,
        options
    }: {
        transactionId: string;
        recipientId: string;
        options?: ApiCallOptions;
    }) => createEsvApiCall<ApiResponseWrapperVerificationView>(
        'GET',
        `transactions/${transactionId}/recipients/${recipientId}/verifications`
    )({...options}),

    createOrUpdateVerification: ({
        transactionId,
        recipientId,
        data,
        options
    }: {
        transactionId: string;
        recipientId: string;
        data: VerificationRequest;
        options?: ApiCallOptions;
    }) => createEsvApiCall<ApiResponseWrapperVerificationView>(
        'PUT',
        `transactions/${transactionId}/recipients/${recipientId}/verifications`
    )({...options, data}),

    deleteVerification: ({
        transactionId,
        recipientId,
        options
    }: {
        transactionId: string;
        recipientId: string;
        options?: ApiCallOptions;
    }) => createEsvApiCall<void>(
        'DELETE',
        `transactions/${transactionId}/recipients/${recipientId}/verifications`
    )({...options}),

    getAccountProviders: ({
        accountId,
        params,
        options
    }: {
        accountId: string;
        params?: PaginationParams;
        options?: ApiCallOptions;
    }) => createEsvApiCall<PageViewAccountProviderView>(
        'GET',
        `accounts/${accountId}/providers`
    )({...options, params}),

    createAccountProvider: ({
        accountId,
        data,
        options
    }: {
        accountId: string;
        data: AccountProviderRequest;
        options?: ApiCallOptions;
    }) => createEsvApiCall<ApiResponseWrapperAccountProviderView>(
        'POST',
        `accounts/${accountId}/providers`
    )({...options, data}),

    getAccountProvider: ({
        accountId,
        providerId,
        options
    }: {
        accountId: string;
        providerId: string;
        options?: ApiCallOptions;
    }) => createEsvApiCall<ApiResponseWrapperAccountProviderView>(
        'GET',
        `accounts/${accountId}/providers/${providerId}`
    )({...options}),

    updateAccountProvider: ({
        accountId,
        providerId,
        data,
        options
    }: {
        accountId: string;
        providerId: string;
        data: UpdateAccountProviderRequest;
        options?: ApiCallOptions;
    }) => createEsvApiCall<ApiResponseWrapperAccountProviderView>(
        'PUT',
        `accounts/${accountId}/providers/${providerId}`
    )({...options, data}),

    deleteAccountProvider: ({
        accountId,
        providerId,
        options
    }: {
        accountId: string;
        providerId: string;
        options?: ApiCallOptions;
    }) => createEsvApiCall<void>(
        'DELETE',
        `accounts/${accountId}/providers/${providerId}`
    )({...options}),

    getProviders: ({
        params,
        options
    }: {
        params?: PaginationParams;
        options?: ApiCallOptions;
    } = {}) => createEsvApiCall<PageViewProviderView>(
        'GET',
        'providers'
    )({...options, params}),

    createProvider: ({
        data,
        options
    }: {
        data: ProviderRequest;
        options?: ApiCallOptions;
    }) => createEsvApiCall<ApiResponseWrapperProviderView>(
        'POST',
        'providers'
    )({...options, data}),

    getProvider: ({
        providerId,
        options
    }: {
        providerId: string;
        options?: ApiCallOptions;
    }) => createEsvApiCall<ApiResponseWrapperProviderView>(
        'GET',
        `providers/${providerId}`
    )({...options}),

    deleteProvider: ({
        providerId,
        options
    }: {
        providerId: string;
        options?: ApiCallOptions;
    }) => createEsvApiCall<void>(
        'DELETE',
        `providers/${providerId}`
    )({...options}),

    generateVerificationToken: ({
        transactionId,
        documentId,
        data,
        options
    }: {
        transactionId: string;
        documentId: string;
        data: VerificationTokenRequest;
        options?: ApiCallOptions;
    }) => createEsvApiCall<ApiResponseWrapperVerificationTokenView>(
        'POST',
        `transactions/${transactionId}/documents/${documentId}/verificationRequestToken`
    )({...options, data}),

    getVerificationRequestToken
};
